import { useEffect, useRef, useState } from 'react';

import { useQuestionInteractionState } from '@/features/content/hooks/useQuestionInteractionState';
import type { UserRole } from '@/features/courses/useCourse';

import { AnswerActionButton } from './AnswerActionButton';
import type { SelectAllQuestionData } from './questionNormalizer';
import { QuestionOptionRow } from './QuestionOptionRow';
import type { NormalizedAnswerInput } from './useQuestionSubmission';
import styles from './MultipleChoiceQuestion.module.css';
import selectAllStyles from './SelectAllQuestion.module.css';

/**
 * The select-all-that-apply control.
 *
 * Deliberately mirrors MultipleChoiceQuestion — same option row, same
 * stylesheet, same interaction state — because the two differ in only three
 * things: checkboxes instead of radios, selecting toggles instead of replaces,
 * and EVERY correct option is marked when the answer is revealed rather than
 * one.
 *
 * Grading is all-or-nothing on the server: the selected set must equal the
 * correct set, order-independent, with no partial credit. Nothing here may
 * imply otherwise.
 */
interface SelectAllQuestionProps {
  areSolutionsVisible: boolean;
  hideStats?: boolean;
  /** The answer is kept on the device until the server has it: no "Saved" tag. */
  isAnswerKept?: boolean;
  isSectionClosed: boolean;
  isSectionOpen: boolean;
  onCheck(answer: NormalizedAnswerInput): Promise<void>;
  /** Told whether the ticks on screen differ from the ones stored. */
  onEntryChange?(isUnsaved: boolean): void;
  onSave(answer: NormalizedAnswerInput): Promise<void>;
  pending: boolean;
  question: SelectAllQuestionData;
  /** The request the service worker kept and the server has since refused. */
  refusedSubmissionId?: string;
  role: UserRole;
  showActions?: boolean;
}

/** Set comparison, matching how the server grades: order and duplicates ignored. */
function sameSelection(a: readonly number[], b: readonly number[]) {
  const left = new Set(a);
  const right = new Set(b);

  return left.size === right.size && [...left].every((index) => right.has(index));
}

export function SelectAllQuestion({
  areSolutionsVisible,
  hideStats = false,
  isAnswerKept = false,
  isSectionClosed,
  isSectionOpen,
  onCheck,
  onEntryChange,
  onSave,
  pending,
  question,
  refusedSubmissionId,
  role,
  showActions = true,
}: SelectAllQuestionProps) {
  const [selectedIndices, setSelectedIndices] = useState<number[]>(
    question.selectedIndices,
  );
  const [savedIndices, setSavedIndices] = useState<number[]>(
    question.selectedIndices,
  );
  const [checkedIndices, setCheckedIndices] = useState<number[] | undefined>(
    question.attemptsUsed > 0 && question.feedback?.status !== 'saved'
      ? question.selectedIndices
      : undefined,
  );
  const previousQuestionIdRef = useRef(question.id);

  useEffect(() => {
    const isSameQuestion = previousQuestionIdRef.current === question.id;
    previousQuestionIdRef.current = question.id;

    // What the student has changed and not saved stays while they can still change
    // it. An older answer replayed from the queue put itself back on screen, and a
    // Check then graded that and used an attempt.
    if (!isSameQuestion || !isEntryUnsaved || interaction.isAnswerLocked) {
      setSelectedIndices(question.selectedIndices);
    }

    setSavedIndices(question.selectedIndices);
    setCheckedIndices((currentCheckedIndices) => {
      if (question.attemptsUsed > 0 && question.feedback?.status !== 'saved') {
        return question.selectedIndices;
      }

      // A save after a check keeps the checked result — but ONLY while the
      // saved selection is still the selection that was checked. Saving a
      // different selection replaces what will be graded at the due date, so
      // the old verdict no longer describes the student's answer.
      if (
        isSameQuestion &&
        question.attemptsUsed > 0 &&
        question.feedback?.status === 'saved'
      ) {
        return currentCheckedIndices !== undefined &&
          sameSelection(question.selectedIndices, currentCheckedIndices)
          ? currentCheckedIndices
          : undefined;
      }

      return undefined;
    });
  }, [
    question.attemptsUsed,
    question.feedback?.status,
    question.id,
    question.selectedIndices,
  ]);

  const hasAnswer = selectedIndices.length > 0;
  const hasSelectionChangedFromSaved =
    hasAnswer && !sameSelection(selectedIndices, savedIndices);
  const hasSavedAnswer = hasAnswer && sameSelection(selectedIndices, savedIndices);
  // A check stores the selection it grades, so Check takes the ticks on
  // screen, saved or not, unless they are the ones last checked (issue #92).
  const isAnswerAlreadyChecked =
    hasAnswer &&
    checkedIndices !== undefined &&
    sameSelection(selectedIndices, checkedIndices);
  const isEntryUnsaved = !sameSelection(selectedIndices, savedIndices);

  useEffect(() => {
    onEntryChange?.(isEntryUnsaved);
  }, [isEntryUnsaved, onEntryChange]);

  const earnedPoints = question.feedback?.earnedPoints ?? question.scoreEarned;
  const maxPointsThreshold =
    question.feedback?.possiblePoints ?? question.pointsPossible;
  const hasEarnedMaxPoints =
    earnedPoints !== undefined &&
    maxPointsThreshold > 0 &&
    earnedPoints >= maxPointsThreshold;
  const interaction = useQuestionInteractionState({
    attemptsAllowed: question.maxAttempts,
    attemptsUsed: question.attemptsUsed,
    areSolutionsVisible,
    hasCheckableAnswer: hasAnswer && !isAnswerAlreadyChecked,
    hasEarnedMaxPoints,
    hasCheckedAtLeastOnce:
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved',
    hasSavedAnswer,
    hasSelectionChangedFromSaved,
    hideStatsAndSolutions: hideStats,
    hintSetting: question.hintSetting,
    isPractice: !showActions || !question.isAssigned,
    isSectionClosed,
    isSectionOpen,
    isSingleAttemptMode: question.isSingleAttemptMode,
    role,
  });

  // Kept by the service worker, this answer was recorded as saved, which took the
  // stored answer's verdict away. The server has now refused it, which changes nothing
  // stored, so the effect above does not run. It stays on screen, as unsaved, while it
  // can still be changed; options that cannot show what is stored.
  useEffect(() => {
    if (!refusedSubmissionId) {
      return;
    }

    if (interaction.isAnswerLocked) {
      setSelectedIndices(question.selectedIndices);
    }

    setSavedIndices(question.selectedIndices);
    setCheckedIndices(
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved'
        ? question.selectedIndices
        : undefined,
    );
  }, [
    interaction.isAnswerLocked,
    question.attemptsUsed,
    question.feedback?.status,
    question.selectedIndices,
    refusedSubmissionId,
  ]);

  const answer: NormalizedAnswerInput = {
    kind: 'selectAll',
    selectedIndices,
  };

  function toggleIndex(index: number) {
    setSelectedIndices((current) =>
      current.includes(index)
        ? current.filter((existing) => existing !== index)
        : [...current, index],
    );
  }

  async function handleSave() {
    if (!hasAnswer) {
      return;
    }

    // onSave rejects when the answer was not stored (the card has told the
    // student). Then nothing below runs: the entry stays unsaved, and Save
    // stays available. It used to be marked "Saved" whatever happened.
    try {
      await onSave(answer);
    } catch {
      return;
    }

    setSavedIndices(selectedIndices);
    // The saved selection is what will be graded from here on, so a verdict
    // from a previously checked DIFFERENT selection no longer applies — that
    // includes the status line below the options, not just the option markers.
    setCheckedIndices((currentCheckedIndices) =>
      currentCheckedIndices !== undefined &&
      sameSelection(currentCheckedIndices, selectedIndices)
        ? currentCheckedIndices
        : undefined,
    );
  }

  async function handleCheck() {
    if (!hasAnswer) {
      return;
    }

    // As for Save: a check that was not stored leaves no verdict behind.
    try {
      await onCheck(answer);
    } catch {
      return;
    }

    setSavedIndices(selectedIndices);
    setCheckedIndices(selectedIndices);
  }

  const showCorrectAnswer = interaction.showCorrectAnswer;
  // Correctness is all-or-nothing and belongs to the SELECTION, never to a
  // single option. This is the verdict for the whole answer; it drives the one
  // status line below the options and nothing per-option.
  const checkedAnswerState =
    question.feedback?.status === 'correct'
      ? ('correct' as const)
      : question.feedback?.status === 'incorrect' ||
          question.feedback?.status === 'partial'
        ? ('incorrect' as const)
        : checkedIndices !== undefined
          ? sameSelection(checkedIndices, question.correctAnswerIndices)
            ? ('correct' as const)
            : ('incorrect' as const)
          : undefined;
  // A verdict is about the ticks it was given. Under any others it reads as the
  // server's word on an answer it has not seen, one that may be right.
  const hasCheckedResult =
    checkedIndices !== undefined &&
    checkedAnswerState !== undefined &&
    sameSelection(selectedIndices, checkedIndices);
  const showDistributionStats =
    question.isInstructorView &&
    !hideStats &&
    (question.answerIndexStats?.length ?? 0) > 0;
  const showActionRow =
    showActions && (interaction.showSaveButton || interaction.showCheckButton);

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Select all that apply</legend>

      <div className={styles.optionList}>
        {question.options.map((option) => {
          const inputId = `${question.id}-option-${option.index}`;
          // Several options can be correct, so every one of them is marked when
          // the answer is revealed — not just the first.
          const isCorrect = question.correctAnswerIndices.includes(option.index);
          const isSelected = selectedIndices.includes(option.index);
          const isSavedOption =
            role === 'student' &&
            question.isAssigned &&
            !isAnswerKept &&
            savedIndices.includes(option.index) &&
            !sameSelection(savedIndices, checkedIndices ?? []);
          const isSubmittedOption =
            role === 'student' &&
            question.isAssigned &&
            checkedIndices !== undefined &&
            checkedIndices.includes(option.index) &&
            checkedAnswerState !== undefined;

          // The verdict shown ON THIS OPTION, which is not the verdict for the
          // selection.
          //
          // Before the answer is revealed there is none: marking every picked
          // option red because the set was wrong tells the student each of those
          // options is wrong, which is a claim the grading never made — and a
          // student could then tick everything, check, and read the answer off
          // the colours.
          //
          // Once the answer IS revealed, per-option marking is the answer
          // itself, so it is judged per option rather than inherited: a picked
          // option that is genuinely correct shows correct even when the
          // selection as a whole fell short.
          const optionState = !isSubmittedOption
            ? undefined
            : showCorrectAnswer
              ? isCorrect
                ? ('correct' as const)
                : ('incorrect' as const)
              : ('submitted' as const);

          return (
            <QuestionOptionRow
              checkedAnswerState={optionState}
              // The stats badge marks the correct option by comparing against a
              // single index. With several correct options there is no one index
              // to compare against, so the badge shows the distribution without a
              // correctness marker rather than marking an arbitrary one.
              correctIndexForStats={undefined}
              disabled={interaction.isAnswerLocked}
              inputId={inputId}
              // Checkboxes must NOT share a name — a shared name is what makes a
              // radio group single-select.
              inputName={inputId}
              inputType="checkbox"
              isCorrect={isCorrect}
              isSaved={isSavedOption}
              isSelected={isSelected}
              isSubmitted={isSubmittedOption}
              key={option.id}
              onToggle={() => {
                toggleIndex(option.index);
              }}
              option={option}
              role={role}
              showCorrectAnswer={showCorrectAnswer}
              showDistributionStats={showDistributionStats}
              stats={question.answerIndexStats}
              units={question.units}
            />
          );
        })}
      </div>

      {hasCheckedResult ? (
        <p
          className={selectAllStyles.result}
          data-status={checkedAnswerState}
          role="status"
        >
          <span className={selectAllStyles.resultTitle}>
            {checkedAnswerState === 'correct'
              ? 'Correct'
              : 'Incorrect selection'}
          </span>
          <span className={selectAllStyles.resultDetail}>
            {checkedAnswerState === 'correct'
              ? 'You selected every correct option and nothing else.'
              : 'Credit requires selecting every correct option and no others, so this selection earns none. Some of the options you picked may still be part of the answer.'}
          </span>
        </p>
      ) : null}

      {showActionRow ? (
        <div className={styles.actions}>
          {interaction.showSaveButton ? (
            <AnswerActionButton
              onPress={() => {
                void handleSave();
              }}
              questionId={question.id}
              unavailable={!hasAnswer || interaction.isSaveButtonDisabled || pending}
              variant="secondary"
            >
              Save answer
            </AnswerActionButton>
          ) : null}
          {interaction.showCheckButton ? (
            <AnswerActionButton
              onPress={() => {
                void handleCheck();
              }}
              questionId={question.id}
              unavailable={!hasAnswer || interaction.isCheckButtonDisabled || pending}
              variant="primary"
            >
              Check answer
            </AnswerActionButton>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
